import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  Grid,
  useMediaQuery,
  useTheme,
  Divider,
  Tooltip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import facturaCompraService, {
  LibroIvaComprasItem
} from '../../services/facturaCompraService';
import { formatCurrency } from '../../utils/formatters';
import { Article as ArticleIcon, FileDownload as FileDownloadIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import InformesBackButton from '../../components/shared/InformesBackButton';
import PDFViewer from '../../components/shared/PDFViewer';
import {
  fetchCompaniaParaPdf,
  fetchLogoPngDataUrl,
  lineasDatosCompania,
  pdfLibroIvaComprasColumnStyles,
  pdfLibroIvaTableWidth,
  pdfFootLabelCell,
  pdfFootAmountCell,
  pdfLibroIvaFootStyles,
  PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS,
  PDF_LIBRO_IVA_COMPRAS_HEAD,
  pdfLibroIvaComprasTableStyles,
  formatCurrencyPdfCompact,
  type PdfFootCellDef
} from '../../utils/pdfLibroIvaCompania';

// Función para formatear fecha en formato DD/MM/AAAA con ceros a la izquierda
const formatearFecha = (fecha: Date | string): string => {
  const date = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(date.getTime())) return '';
  
  const dia = date.getDate().toString().padStart(2, '0');
  const mes = (date.getMonth() + 1).toString().padStart(2, '0');
  const año = date.getFullYear();
  
  return `${dia}/${mes}/${año}`;
};

interface LibroIvaComprasRow {
  fechaEmision: string;
  tipoComprobante: string;
  puntoVenta: string;
  numeroComprobante: string;
  proveedor: string;
  cuit: string;
  tipoIva: string;
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  perIibb: number;
  perIva: number;
  impInt: number;
  impMun: number;
  impNac: number;
  total: number;
  nroFormulario: number;
}

interface LibroIvaComprasTotales {
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  perIibb: number;
  perIva: number;
  impInt: number;
  impMun: number;
  impNac: number;
  total: number;
}

const LIBRO_IVA_TABLE_COL_COUNT = 18;

const totalesComprasInicial: LibroIvaComprasTotales = {
  netoGravado: 0,
  iva21: 0,
  iva105: 0,
  iva27: 0,
  exento: 0,
  perIibb: 0,
  perIva: 0,
  impInt: 0,
  impMun: 0,
  impNac: 0,
  total: 0
};

const sumLibroIvaComprasRows = (rows: LibroIvaComprasRow[]): LibroIvaComprasTotales =>
  rows.reduce(
    (acc, row) => ({
      netoGravado: acc.netoGravado + row.netoGravado,
      iva21: acc.iva21 + row.iva21,
      iva105: acc.iva105 + row.iva105,
      iva27: acc.iva27 + row.iva27,
      exento: acc.exento + row.exento,
      perIibb: acc.perIibb + row.perIibb,
      perIva: acc.perIva + row.perIva,
      impInt: acc.impInt + row.impInt,
      impMun: acc.impMun + row.impMun,
      impNac: acc.impNac + row.impNac,
      total: acc.total + row.total
    }),
    { ...totalesComprasInicial }
  );

const buildComprasFootRowPdf = (totales: LibroIvaComprasTotales): PdfFootCellDef[] => [
  pdfFootLabelCell('Totales', 7),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.netoGravado), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.iva21), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.iva105), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.iva27), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.exento), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.perIibb), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.perIva), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.impInt), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.impMun), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.impNac), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
  pdfFootAmountCell(formatCurrencyPdfCompact(totales.total), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS)
];

const footerCellSx = { fontWeight: 700, bgcolor: '#f0f4f8', borderTop: '2px solid #bdbdbd' };

// Función para formatear el tipo de IVA
const formatearTipoIva = (tipoIva?: string): string => {
  if (!tipoIva) {
    return '-';
  }

  const normalized = tipoIva.toUpperCase();

  if (normalized === 'I' || normalized.includes('RESPONSABLE') || normalized.includes('INSCRIPTO')) {
    return 'Resp. Insc.';
  }

  if (normalized === 'M' || normalized.includes('MONOTRIBUTO')) {
    return 'Monotributo';
  }

  if (normalized === 'E' || normalized.includes('EXENTO')) {
    return 'Exento';
  }

  if (normalized === 'C' || normalized.includes('CONSUMIDOR') || normalized.includes('FINAL')) {
    return 'Cons. Final';
  }

  if (normalized === 'N' || normalized.includes('NO') || normalized.includes('CATEGORIZADO')) {
    return 'No Categorizado';
  }

  return tipoIva;
};

const LibroIvaCompras: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [facturas, setFacturas] = useState<LibroIvaComprasItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingTxt, setExportingTxt] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;
  const [totalesInforme, setTotalesInforme] = useState<LibroIvaComprasTotales>(totalesComprasInicial);

  // Inicializar fechas: primer día del mes actual y último día del mes actual
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [filters, setFilters] = useState({
    fechaDesde: firstDayOfMonth.toISOString().split('T')[0],
    fechaHasta: lastDayOfMonth.toISOString().split('T')[0],
    nroProveedor: '',
    nroSuc: '',
    nombreProveedor: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await facturaCompraService.getLibroIvaCompras({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        fechaDesde: filters.fechaDesde ? new Date(filters.fechaDesde) : undefined,
        fechaHasta: filters.fechaHasta ? new Date(filters.fechaHasta) : undefined,
        nroProveedor: filters.nroProveedor ? parseInt(filters.nroProveedor) : undefined,
        nroSuc: filters.nroSuc ? parseInt(filters.nroSuc) : undefined,
        nombreProveedor: filters.nombreProveedor || undefined
      });

      setFacturas(response.items);
      setTotalCount(response.totalCount);
      setTotalesInforme({
        netoGravado: response.totalNetoGrav ?? 0,
        iva21: response.totalIva21 ?? 0,
        iva105: response.totalIva10 ?? 0,
        iva27: response.totalIva27 ?? 0,
        exento: response.totalExento ?? 0,
        perIibb: response.totalPerIibb ?? 0,
        perIva: response.totalPerIva ?? 0,
        impInt: response.totalImpInt ?? 0,
        impMun: response.totalImpMun ?? 0,
        impNac: response.totalImpNac ?? 0,
        total: response.totalImporteTotal ?? 0
      });
    } catch (error) {
      console.error('Error al obtener el libro IVA compras:', error);
      enqueueSnackbar('No se pudieron cargar los datos del libro IVA.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, page, rowsPerPage, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const handleClosePdfPreview = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const name = event.target.name || '';
    const value = event.target.value;
    setFilters({ ...filters, [name]: value });
    setPage(0);
  };

  // Convertir items del API a filas del libro IVA (usa netoGrav, iva10, iva21, iva27, exento del DTO)
  const convertToLibroIvaRows = (items: LibroIvaComprasItem[]): LibroIvaComprasRow[] => {
    return items.map(item => {
      const tipoComprobante =
        (item.documento ?? '').trim()
        || (item.nroTipoFormulario ?? '').trim()
        || '-';

      const netoGravado = item.netoGrav ?? 0;
      const iva21 = item.iva21 ?? 0;
      const iva105 = item.iva10 ?? 0;
      const iva27 = item.iva27 ?? 0;
      const exento = item.exento ?? 0;
      const perIibb = item.perIibb ?? 0;
      const perIva = item.perIva ?? 0;
      const impInt = item.impInt ?? 0;
      const impMun = item.impMun ?? 0;
      const impNac = item.impNac ?? 0;
      const total = item.importeTotal ?? 0;

      const puntoVentaFormateado = (item.nroSuc ?? 0).toString().padStart(5, '0');
      const numeroComprobanteFormateado = (item.nroComprobante ?? 0).toString().padStart(8, '0');
      const fechaEmisionStr = item.fechaemision ? String(item.fechaemision) : '';

      return {
        fechaEmision: fechaEmisionStr,
        tipoComprobante,
        puntoVenta: puntoVentaFormateado,
        numeroComprobante: numeroComprobanteFormateado,
        proveedor: item.nombre || '-',
        cuit: item.cuit || '-',
        tipoIva: formatearTipoIva(item.tipoIva),
        netoGravado,
        iva21,
        iva105,
        iva27,
        exento,
        perIibb,
        perIva,
        impInt,
        impMun,
        impNac,
        total,
        nroFormulario: item.nroformulario
      };
    });
  };

  // Función auxiliar para obtener todas las páginas de resultados
  const obtenerTodasLasPaginas = async (): Promise<LibroIvaComprasItem[]> => {
    const todasLasFacturas: LibroIvaComprasItem[] = [];
    const pageSize = 20; // Límite del backend
    let pageNumber = 1;
    let totalPages = 1;

    do {
      const response = await facturaCompraService.getLibroIvaCompras({
        pageNumber,
        pageSize,
        fechaDesde: filters.fechaDesde ? new Date(filters.fechaDesde) : undefined,
        fechaHasta: filters.fechaHasta ? new Date(filters.fechaHasta) : undefined,
        nroProveedor: filters.nroProveedor ? parseInt(filters.nroProveedor) : undefined,
        nroSuc: filters.nroSuc ? parseInt(filters.nroSuc) : undefined,
        nombreProveedor: filters.nombreProveedor || undefined
      });

      todasLasFacturas.push(...response.items);
      totalPages = response.totalPages;
      pageNumber++;
    } while (pageNumber <= totalPages);

    return todasLasFacturas;
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExporting(true);
      const todasLasFacturas = await obtenerTodasLasPaginas();

      if (!todasLasFacturas.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      const rows = convertToLibroIvaRows(todasLasFacturas);
      const totalesExport = sumLibroIvaComprasRows(rows);

      const data = rows.map((row) => ({
        'Fecha': row.fechaEmision ? formatearFecha(row.fechaEmision) : '',
        'Tipo': row.tipoComprobante,
        'Punto Venta': row.puntoVenta,
        'Número': row.numeroComprobante,
        'Proveedor': row.proveedor,
        'CUIT': row.cuit,
        'Tipo IVA': row.tipoIva,
        'Neto Gravado': row.netoGravado,
        'IVA 21%': row.iva21,
        'IVA 10.5%': row.iva105,
        'IVA 27%': row.iva27,
        'Exento': row.exento,
        'Perc.IIBB': row.perIibb,
        'Perc.IVA': row.perIva,
        'Imp.Int.': row.impInt,
        'Imp.Mun.': row.impMun,
        'Imp.Nac.': row.impNac,
        'Importe total': row.total
      }));

      data.push({
        'Fecha': 'Totales',
        'Tipo': '',
        'Punto Venta': '',
        'Número': '',
        'Proveedor': '',
        'CUIT': '',
        'Tipo IVA': '',
        'Neto Gravado': totalesExport.netoGravado,
        'IVA 21%': totalesExport.iva21,
        'IVA 10.5%': totalesExport.iva105,
        'IVA 27%': totalesExport.iva27,
        'Exento': totalesExport.exento,
        'Perc.IIBB': totalesExport.perIibb,
        'Perc.IVA': totalesExport.perIva,
        'Imp.Int.': totalesExport.impInt,
        'Imp.Mun.': totalesExport.impMun,
        'Imp.Nac.': totalesExport.impNac,
        'Importe total': totalesExport.total
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Libro IVA Compras');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `libro_iva_compras_${today}.xlsx`);
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const parseInputDate = (value: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const downloadTextFile = (content: string, fileName: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportTxtArca = async () => {
    const fechaDesde = parseInputDate(filters.fechaDesde);
    const fechaHasta = parseInputDate(filters.fechaHasta);

    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Debe seleccionar fecha desde y fecha hasta para exportar TXT.', { variant: 'warning' });
      return;
    }

    if (fechaDesde > fechaHasta) {
      enqueueSnackbar('La fecha desde no puede ser mayor que la fecha hasta.', { variant: 'warning' });
      return;
    }

    try {
      setExportingTxt(true);
      const result = await facturaCompraService.exportLibroIvaComprasArcaTxt({
        fechaDesde,
        fechaHasta,
      });

      downloadTextFile(result.comprobantesContent, result.comprobantesFileName);
      downloadTextFile(result.alicuotasContent, result.alicuotasFileName);
      enqueueSnackbar('Archivos TXT generados para ARCA.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar TXT ARCA:', error);
      enqueueSnackbar('No se pudieron generar los archivos TXT para ARCA.', { variant: 'error' });
    } finally {
      setExportingTxt(false);
    }
  };

  const handlePreviewPdfLibroIva = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay información para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      const todasLasFacturas = await obtenerTodasLasPaginas();

      if (!todasLasFacturas.length) {
        enqueueSnackbar('No hay información para exportar.', { variant: 'warning' });
        return;
      }

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const pageW = doc.internal.pageSize.getWidth();
      const headerTop = 32;
      const logoMaxW = 76;
      const logoMaxH = 42;
      const rightColW = 228;
      const rightX = pageW - marginX;

      const compania = await fetchCompaniaParaPdf();
      const logoPng = compania?.idCompania
        ? await fetchLogoPngDataUrl(compania.idCompania)
        : null;
      if (logoPng) {
        doc.addImage(logoPng, 'PNG', marginX, headerTop, logoMaxW, logoMaxH, undefined, 'FAST');
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(25, 55, 95);
      doc.text('Libro IVA Compras', pageW / 2, headerTop + 26, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(7);
      const datosComp = compania ? lineasDatosCompania(compania) : [];
      let ry = headerTop + 4;
      // Sin maxWidth en align:right: jsPDF justifica y abre huecos enormes entre palabras.
      datosComp.forEach((linea) => {
        const wrapped = doc.splitTextToSize(linea, rightColW);
        wrapped.forEach((sub: string) => {
          doc.text(sub, rightX, ry, { align: 'right' });
          ry += 8;
        });
      });

      const headerBottom = Math.max(headerTop + logoMaxH + 4, ry + 4, headerTop + 48);
      let currentY = headerBottom;

      doc.setFontSize(8);
      const generadoEn = new Date();
      doc.text(`Generado: ${generadoEn.toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 12;

      if (filters.fechaDesde && filters.fechaHasta) {
        const fechaDesdeParts = filters.fechaDesde.split('-');
        const fechaHastaParts = filters.fechaHasta.split('-');
        const fechaDesdeLocal = new Date(
          parseInt(fechaDesdeParts[0], 10),
          parseInt(fechaDesdeParts[1], 10) - 1,
          parseInt(fechaDesdeParts[2], 10)
        );
        const fechaHastaLocal = new Date(
          parseInt(fechaHastaParts[0], 10),
          parseInt(fechaHastaParts[1], 10) - 1,
          parseInt(fechaHastaParts[2], 10)
        );
        doc.text(
          `Aplicación IVA: ${formatearFecha(fechaDesdeLocal)} - ${formatearFecha(fechaHastaLocal)}`,
          marginX,
          currentY
        );
        currentY += 14;
      }

      const rows = convertToLibroIvaRows(todasLasFacturas);
      const totalesExport = sumLibroIvaComprasRows(rows);

      const autoTable = (await import('jspdf-autotable')).default;
      const tableWidth = pdfLibroIvaTableWidth(pageW, marginX);
      const comprasTableStyles = pdfLibroIvaComprasTableStyles();

      autoTable(doc, {
        startY: currentY,
        tableWidth,
        head: [Array.from(PDF_LIBRO_IVA_COMPRAS_HEAD)],
        body: rows.map((row) => [
          row.fechaEmision ? formatearFecha(row.fechaEmision) : '-',
          row.tipoComprobante,
          row.puntoVenta,
          row.numeroComprobante,
          row.proveedor,
          row.cuit,
          row.tipoIva,
          formatCurrencyPdfCompact(row.netoGravado),
          formatCurrencyPdfCompact(row.iva21),
          formatCurrencyPdfCompact(row.iva105),
          formatCurrencyPdfCompact(row.iva27),
          formatCurrencyPdfCompact(row.exento),
          formatCurrencyPdfCompact(row.perIibb),
          formatCurrencyPdfCompact(row.perIva),
          formatCurrencyPdfCompact(row.impInt),
          formatCurrencyPdfCompact(row.impMun),
          formatCurrencyPdfCompact(row.impNac),
          formatCurrencyPdfCompact(row.total)
        ]),
        foot: [buildComprasFootRowPdf(totalesExport)],
        showFoot: 'lastPage',
        margin: { left: marginX, right: marginX },
        ...comprasTableStyles,
        footStyles: pdfLibroIvaFootStyles(PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS),
        columnStyles: pdfLibroIvaComprasColumnStyles(tableWidth)
      });

      const pdfBlob = doc.output('blob');
      const safeBlob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error: unknown) {
      if ((error as { name?: string })?.name === 'AbortError') {
        enqueueSnackbar('Exportación cancelada por el usuario.', { variant: 'warning' });
      } else {
        console.error('Error al generar el PDF:', error);
        enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const renderFooter = () => {
    if (loading || facturas.length === 0) {
      return null;
    }

    return (
      <TableRow>
        <TableCell colSpan={7} sx={footerCellSx}>
          Totales
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesInforme.netoGravado)}
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesInforme.iva21)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesInforme.iva105)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesInforme.iva27)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesInforme.exento)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesInforme.perIibb)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesInforme.perIva)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesInforme.impInt)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesInforme.impMun)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
          {formatCurrency(totalesInforme.impNac)}
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesInforme.total)}
        </TableCell>
      </TableRow>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={LIBRO_IVA_TABLE_COL_COUNT} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!loading && facturas.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={LIBRO_IVA_TABLE_COL_COUNT} align="center">
            No hay facturas para el período seleccionado.
          </TableCell>
        </TableRow>
      );
    }

    const rows = convertToLibroIvaRows(facturas);

    return (
      <>
        {rows.map((row, index) => (
          <TableRow key={`${row.nroFormulario}-${index}`} hover>
            <TableCell>
              {row.fechaEmision ? formatearFecha(row.fechaEmision) : '-'}
            </TableCell>
            <TableCell>{row.tipoComprobante}</TableCell>
            <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
              {row.puntoVenta}
            </TableCell>
            <TableCell align="center">{row.numeroComprobante}</TableCell>
            <TableCell>{row.proveedor}</TableCell>
            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{row.cuit}</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.tipoIva}</TableCell>
            <TableCell align="right">{formatCurrency(row.netoGravado)}</TableCell>
            <TableCell align="right">{formatCurrency(row.iva21)}</TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
              {formatCurrency(row.iva105)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
              {formatCurrency(row.iva27)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
              {formatCurrency(row.exento)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.perIibb)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.perIva)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.impInt)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.impMun)}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
              {formatCurrency(row.impNac)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {formatCurrency(row.total)}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const renderCards = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (!loading && facturas.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography variant="body1" color="text.secondary">
            No hay facturas para el período seleccionado.
          </Typography>
        </Box>
      );
    }

    const rows = convertToLibroIvaRows(facturas);

    return (
      <Stack spacing={2}>
        {rows.map((row, index) => (
          <Card
            key={`${row.nroFormulario}-${index}`}
            sx={{
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {row.tipoComprobante}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.fechaEmision ? formatearFecha(row.fechaEmision) : '-'}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {formatCurrency(row.total)}
                  </Typography>
                </Stack>

                <Divider />

                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      P.Vta / Número:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.puntoVenta} - {row.numeroComprobante}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Proveedor:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
                      {row.proveedor}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      CUIT:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.cuit}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Tipo IVA:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.tipoIva}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider />

                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Neto Gravado:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(row.netoGravado)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      IVA 21%:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(row.iva21)}
                    </Typography>
                  </Stack>
                  {row.iva105 !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        IVA 10.5%:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.iva105)}
                      </Typography>
                    </Stack>
                  )}
                  {row.iva27 !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        IVA 27%:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.iva27)}
                      </Typography>
                    </Stack>
                  )}
                  {row.exento !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Exento:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.exento)}
                      </Typography>
                    </Stack>
                  )}
                  {row.perIibb !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Perc.IIBB:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.perIibb)}</Typography>
                    </Stack>
                  )}
                  {row.perIva !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Perc.IVA:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.perIva)}</Typography>
                    </Stack>
                  )}
                  {row.impInt !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Imp.Int.:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.impInt)}</Typography>
                    </Stack>
                  )}
                  {row.impMun !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Imp.Mun.:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.impMun)}</Typography>
                    </Stack>
                  )}
                  {row.impNac !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Imp.Nac.:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(row.impNac)}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  const renderMobileTotales = () => {
    if (loading || facturas.length === 0) {
      return null;
    }

    return (
      <Card sx={{ mt: 2, borderRadius: 2, border: '2px solid #bdbdbd', bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Totales del informe
          </Typography>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Neto Gravado</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.netoGravado)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 21%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.iva21)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 10.5%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.iva105)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 27%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.iva27)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Exento</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.exento)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Perc.IIBB</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.perIibb)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Perc.IVA</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.perIva)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Imp.Int.</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.impInt)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Imp.Mun.</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.impMun)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Imp.Nac.</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.impNac)}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1" sx={{ fontWeight: 700 }}>Importe total</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2' }}>{formatCurrency(totalesInforme.total)}</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <InformesBackButton to="/compras/informes" useBrowserBack />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: '#1976d2'
                }}
              >
                Libro IVA Compras
              </Typography>
            </Stack>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={1}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={loading || exporting}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)',
                  }
                }}
              >
                Exportar Excel
              </Button>
              <Tooltip title="Genera los archivos TXT de comprobantes y alícuotas para la presentación del Libro IVA Digital Compras ante ARCA.">
                <Box component="span" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArticleIcon />}
                    onClick={() => void handleExportTxtArca()}
                    disabled={loading || exportingTxt}
                    sx={{
                      borderRadius: 2,
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      width: { xs: '100%', sm: 'auto' },
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {exportingTxt ? 'Generando TXT…' : 'Exportar TXT'}
                  </Button>
                </Box>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handlePreviewPdfLibroIva()}
                disabled={loading || exportingPdf}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {exportingPdf ? 'Generando PDF…' : 'Imprimir PDF'}
              </Button>
            </Stack>
          </Stack>

          <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha Aplicacion IVA desde"
                    type="date"
                    name="fechaDesde"
                    value={filters.fechaDesde}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha Aplicacion IVA hasta"
                    type="date"
                    name="fechaHasta"
                    value={filters.fechaHasta}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nro. Proveedor"
                    name="nroProveedor"
                    value={filters.nroProveedor}
                    onChange={handleFilterChange}
                    size="small"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nro. Sucursal"
                    name="nroSuc"
                    value={filters.nroSuc}
                    onChange={handleFilterChange}
                    size="small"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nombre Proveedor"
                    name="nombreProveedor"
                    value={filters.nombreProveedor}
                    onChange={handleFilterChange}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {isMobile ? (
            <Box>
              {renderCards()}
              {renderMobileTotales()}
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  mt: 2,
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                overflowX: 'auto',
                maxWidth: '100%',
                '& .MuiTableCell-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }
              }}
            >
              <Table size="small" sx={{ minWidth: 1680 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 80, sm: 100 } }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 } }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'center', minWidth: { xs: 60, sm: 80 }, display: { xs: 'none', md: 'table-cell' } }}>P.Vta</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'center', minWidth: { xs: 80, sm: 100 } }}>Número</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 150, sm: 200 } }}>Proveedor</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 }, display: { xs: 'none', lg: 'table-cell' } }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 }, display: { xs: 'none', md: 'table-cell' } }}>Tipo IVA</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 100, sm: 120 } }}>Neto Gravado</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 } }}>IVA 21%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>IVA 10.5%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>IVA 27%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>Exento</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Perc.IIBB</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Perc.IVA</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Imp.Int.</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Imp.Mun.</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: 88, display: { xs: 'none', xl: 'table-cell' } }}>Imp.Nac.</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 100, sm: 120 } }}>Importe total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderBody()}
                  {renderFooter()}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: { xs: 1, sm: 2 }
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Libro IVA Compras"
          documentId="libro-iva-compras-preview"
          documentType="informeCliente"
          downloadFileName={`libro_iva_compras_${new Date().toISOString().slice(0, 10)}.pdf`}
        />
      ) : null}
    </Box>
  );
};

export default LibroIvaCompras;

